import { Loader2, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { SelectField } from "../../components/SelectField";
import { Panel } from "./fields";
import { KeyField, SaveButton, useIntegrationForm } from "./integrationForm";

const KEYS = ["SMTP_HOST", "SMTP_PORT", "SMTP_SECURITY", "SMTP_USERNAME", "SMTP_PASSWORD", "SMTP_FROM_EMAIL", "SMTP_FROM_NAME"];
const SMTP_KEYS = KEYS;

const TestSend = () => {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/smtp/test", { to });
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 flex flex-wrap items-end gap-3">
      <label className="field min-w-[260px] flex-1">
        <span className="field-label">Send a test email to</span>
        <input type="email" className="field-input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@hotel.com" data-testid="smtp-test-to" />
      </label>
      <button type="button" className="btn-outline" onClick={send} disabled={busy || !to} data-testid="smtp-test-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send test
      </button>
    </div>
  );
};

export const EmailTab = () => {
  const { data, form, set, save, busy } = useIntegrationForm(KEYS);
  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;
  const smtp = data.email_mode === "smtp";
  const MODE = {
    smtp: "YOUR MAIL SERVER: system emails go out from your own address",
    system: "PLATFORM MAIL SERVER: emails go out from the platform's address until you add your own below",
    "built-in": "BUILT-IN SERVICE: add your mail server below to send from your own address",
  };
  const p = { form, set };
  return (
    <div className="space-y-6" data-testid="admin-email-tab">
      <div className={`rounded-2xl p-4 text-sm ${smtp ? "bg-brand-light" : "bg-gold/15"} text-ink`} data-testid="email-mode-status">
        Email sending: <b>{MODE[data.email_mode] || MODE["built-in"]}</b>
      </div>
      <Panel title="Mail server (SMTP)" hint="One central reservations address for this whole site, used for confirmations, reminders and staff alerts. If your server fails, emails go out through the built-in service instead. The password stays hidden after saving.">
        <div className="grid gap-5 md:grid-cols-2">
          <KeyField label="SMTP server" name="SMTP_HOST" hint="mail.yourhotel.com" {...p} testid="smtp-host" />
          <SelectField label="Security" value={form.SMTP_SECURITY || "starttls"} testid="smtp-security-select" onChange={(v) => set({ SMTP_SECURITY: v })}
            options={[{ value: "starttls", label: "STARTTLS (usually port 587)" }, { value: "ssl", label: "SSL/TLS (usually port 465)" }, { value: "none", label: "None (not recommended)" }]} />
          <KeyField label="Port" name="SMTP_PORT" hint={form.SMTP_SECURITY === "ssl" ? "465" : "587"} {...p} testid="smtp-port" />
          <KeyField label="Username" name="SMTP_USERNAME" {...p} testid="smtp-username" />
          <KeyField label="Password" name="SMTP_PASSWORD" secret {...p} testid="smtp-password" />
          <KeyField label="Sender email (central reservations address)" name="SMTP_FROM_EMAIL" hint="reservations@yourhotel.com" {...p} testid="smtp-from-email" />
          <KeyField label="Sender name" name="SMTP_FROM_NAME" hint="Reservations team" {...p} testid="smtp-from-name" />
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <SaveButton busy={busy} onClick={() => save()} testid="smtp-save" />
          {smtp && (
            <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="smtp-remove"
              onClick={() => window.confirm("Remove the mail server? Emails go back to the built-in service.") && save({}, SMTP_KEYS)}>
              Remove mail server
            </button>
          )}
        </div>
        {smtp && <TestSend />}
      </Panel>
    </div>
  );
};
